import { Card, CardContent, Stack, Typography } from '@mui/material';
import { EmptyResults } from '../components/EmptyResults';
import SeoHead from '../components/SeoHead';
// TODO(@dvgup/branding migration): replace with `@dvgup/branding`'s
// runtime export once TODO-013 lands. The current fixture supplies
// the `share.*` slot consumed by `<SeoHead>` below.
import { brandingFixture } from '../lib/branding/branding-fixture';

/**
 * 404 / page-not-found surface (task 9.4, public-website spec).
 *
 * `<NotFoundPage>` is mounted at the catch-all route `*` (wired in
 * task 9.5) so any URL that does not match one of the SPA's
 * declared routes lands here per **Requirement 3.6**.
 *
 * **Layout.** A single MUI `<Card>` whose body renders:
 *
 *   1. An `<h1>` "Page not found" heading (`<Typography variant="h1">`).
 *   2. A short body paragraph explaining the URL did not match a
 *      known route.
 *   3. The `<EmptyResults>` component (task 5.4) which surfaces the
 *      five alternative-navigation links — `Daily`, `Days`, `Games`,
 *      `Newsletter`, `Podcast` — exactly as Requirement 3.6 calls
 *      for. Reusing `<EmptyResults>` keeps the empty-collection
 *      experience and the not-found experience visually identical
 *      and lets `BrandingConfig.copy.emptyResults` (gated on
 *      TODO-013 brand assets) control both surfaces from a single
 *      slot per Requirement 8.1 / 8.4.
 *
 * The catch-all route is intentionally an unauthenticated surface
 * — `<RequireAuth>` is **not** applied per Requirement 5.9 — so a
 * mistyped URL never bounces an unauthenticated visitor through
 * `/sign-in`.
 *
 * _Validates: Requirement 3.6 (404 catch-all renders an MUI Card
 * with a "page not found" message + `<EmptyResults>` alternative-
 * navigation suggestions), Requirement 8.1 (alternative-navigation
 * suggestions sourced from `<EmptyResults>`), R3.2 (component
 * factored as a single page-level React function)._
 */
export default function NotFoundPage(): JSX.Element {
  return (
    <Card sx={{ maxWidth: 720, mx: 'auto', mt: 4 }}>
      <CardContent>
        <SeoHead
          route="/404"
          pageTitle="Page not found"
          branding={brandingFixture.share}
        />
        <Stack spacing={2}>
          <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem' }}>
            Page not found
          </Typography>
          <Typography variant="body1">
            We couldn&apos;t find that page. It may have moved, or the link may
            be out of date.
          </Typography>
          <EmptyResults />
        </Stack>
      </CardContent>
    </Card>
  );
}
